import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, TrendingDown, Activity, Clock, Star, Users, Target, BarChart3 } from 'lucide-react';

const RankingSystemPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <h1 className="text-4xl md:text-5xl font-bold gradient-text">
          Ranking System
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
          Understanding how we rank CS2 skins based on professional player usage and team performance
        </p>
      </motion.div>

      {/* Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-8"
      >
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
          <Target className="h-6 w-6 text-primary-400" />
          <span>Overview</span>
        </h2>
        <p className="text-gray-300 leading-relaxed">
          Our CS2 skins ranking system is designed to showcase the most popular and prestigious skins used by professional players. 
          Unlike traditional rankings based on market prices or personal opinions, our system reflects actual competitive usage 
          weighted by team performance in professional matches.
        </p>
      </motion.div>

      {/* How It Works */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-8"
      >
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
          <BarChart3 className="h-6 w-6 text-primary-400" />
          <span>How Rankings Work</span>
        </h2>
        
        <div className="space-y-6">
          {/* Team-Based Scoring */}
          <div className="bg-gray-700 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Trophy className="h-8 w-8 text-cs2-gold" />
              <h3 className="text-xl font-semibold text-white">Team-Based Scoring</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Each time a skin is used in a professional match, it receives points based on the team's current HLTV ranking:
            </p>
            <div className="bg-gray-800 rounded-lg p-4">
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-cs2-gold font-semibold">#1 Team:</span>
                  <span className="text-white">1.00 points</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-300">#2 Team:</span>
                  <span className="text-white">0.99 points</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-300">#10 Team:</span>
                  <span className="text-white">0.91 points</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-300">#50 Team:</span>
                  <span className="text-white">0.51 points</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-400">#100 Team:</span>
                  <span className="text-white">0.01 points</span>
                </li>
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Formula: Score = 1.01 - (team_rank × 0.01)
              </p>
            </div>
          </div>

          {/* Daily Decay */}
          <div className="bg-gray-700 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TrendingDown className="h-8 w-8 text-purple-400" />
              <h3 className="text-xl font-semibold text-white">Daily Decay System</h3>
            </div>
            <p className="text-gray-300 mb-4">
              To ensure rankings remain current and reflect recent meta changes, all skin scores automatically decrease by 10% each day:
            </p>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-white font-semibold">Day 1</div>
                  <div className="text-primary-400">100 points</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold">Day 7</div>
                  <div className="text-orange-400">48.3 points</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold">Day 14</div>
                  <div className="text-red-400">23.3 points</div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center">
                This ensures only recently used skins maintain high rankings
              </p>
            </div>
          </div>

          {/* Live Updates */}
          <div className="bg-gray-700 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Activity className="h-8 w-8 text-green-400" />
              <h3 className="text-xl font-semibold text-white">Live Updates</h3>
            </div>
            <p className="text-gray-300">
              Rankings update automatically as we process new professional matches. Our system monitors HLTV for new demo files 
              and processes them to extract skin usage data, ensuring our rankings always reflect the current professional meta.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card p-8"
      >
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
          <Star className="h-6 w-6 text-primary-400" />
          <span>Weapon Categories</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: 'Rifles', description: 'AK-47, M4A4, M4A1-S, AWP, and other primary weapons', icon: '🔫' },
            { name: 'Pistols', description: 'Glock, USP-S, Desert Eagle, and other sidearms', icon: '🔫' },
            { name: 'SMGs', description: 'MP9, MAC-10, P90, and other submachine guns', icon: '🔫' },
            { name: 'Shotguns', description: 'Nova, XM1014, and other close-range weapons', icon: '🔫' },
            { name: 'Machine Guns', description: 'M249, Negev, and other heavy weapons', icon: '🔫' },
            { name: 'Knives', description: 'Karambit, Butterfly, Bayonet, and other melee weapons', icon: '🔪' }
          ].map((category, index) => (
            <div key={category.name} className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <span className="text-2xl">{category.icon}</span>
                <h3 className="font-semibold text-white">{category.name}</h3>
              </div>
              <p className="text-sm text-gray-400">{category.description}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Data Sources */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card p-8"
      >
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
          <Users className="h-6 w-6 text-primary-400" />
          <span>Data Sources</span>
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              1
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">Match Data</h3>
              <p className="text-gray-400 text-sm">
                Global CS2 Team rankings and match information
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              2
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">Professional Demo Files</h3>
              <p className="text-gray-400 text-sm">
                Official CS2 demo files from professional matches, parsed to extract skin usage information
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              3
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">Skin Database</h3>
              <p className="text-gray-400 text-sm">
                Comprehensive skin information including names, images, and metadata for accurate identification
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Technical Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card p-8"
      >
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
          <Clock className="h-6 w-6 text-primary-400" />
          <span>Update Schedule</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-2">Match Processing</h3>
            <p className="text-gray-400 text-sm">
              New professional matches are processed automatically when demo files become available, 
              typically within hours of match completion.
            </p>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-2">Daily Decay</h3>
            <p className="text-gray-400 text-sm">
              Score decay is applied daily at 02:00 UTC to ensure rankings reflect current trends 
              and meta shifts in the professional scene.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-center py-8"
      >
        <a 
          href="/" 
          className="btn-primary inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <Trophy className="h-5 w-5" />
          <span>View Current Rankings</span>
        </a>
      </motion.div>
    </div>
  );
};

export default RankingSystemPage;
